import React from "react";
import { Button, Spin } from "../ui";
import { useUiLanguage } from "../ui/uiText";
import { FullScreenWaitBackdrop } from "../components/FullScreenWaitBackdrop";
import { spacing, typography } from "../layout/spacing";
import { sharedStartupGateTranslations, type SharedStartupGateLang } from "./startupGateTranslations";
import { requestSharedAppReload } from "./appReloadCoordinator";
import { useStartupGate, type StartupGateOptions } from "./useStartupGate";

export type StartupGateLabels = {
  updatingTitle: React.ReactNode;
  unavailableTitle: React.ReactNode;
  updatingHint: React.ReactNode;
  unavailableHint: React.ReactNode;
};
export type StartupGateOverlayProps = StartupGateOptions & { labels?: StartupGateLabels; zIndex?: number };

export function StartupGateOverlay(props: StartupGateOverlayProps) {
  const language = useUiLanguage();
  const text = sharedStartupGateTranslations[language as SharedStartupGateLang] ?? sharedStartupGateTranslations.en;
  const state = useStartupGate(props);
  if (!state.visible) return null;
  const unavailable = state.unavailable;
  const title = unavailable ? (props.labels?.unavailableTitle ?? text["The application is currently unavailable"])
    : state.manualReload ? text["UI deploy reload failed title"]
    : props.labels ? props.labels.updatingTitle
    : state.stage === "checking" ? text["Database is being checked…"]
    : state.stage === "migrating" ? text["Database is being updated…"]
    : state.stage === "deploy" ? text["Application update in progress"]
    : state.stage === "connection" ? text["Application is loading…"]
    : text["Application is loading…"];
  const hint = unavailable ? (props.labels?.unavailableHint ?? text["Startup unavailable hint"])
    : state.manualReload ? text["UI deploy reload failed hint"]
    : props.labels ? props.labels.updatingHint
    : state.stage === "checking" ? text["Startup check hint"]
    : state.stage === "migrating" ? text["Startup migration hint"]
    : state.stage === "deploy" ? text["Startup deploy hint"]
    : state.stage === "connection" ? text["Startup connection hint"]
    : text["Startup loading hint"];
  return (
    <FullScreenWaitBackdrop opaque zIndex={props.zIndex ?? 9999}>
      <div style={{ width: "min(720px, 100%)", display: "grid", gap: spacing.sectionGap, textAlign: "center" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: spacing.sectionGap }}>
          {!state.manualReload && !unavailable && <Spin size="large" />}
          <div style={{ fontSize: typography.titleMd, fontWeight: 600 }}>{title}</div>
        </div>
        <div style={{ opacity: 0.75, fontSize: typography.base }}>{hint}</div>
        {state.manualReload && !unavailable && <div><Button type="primary" onClick={() => requestSharedAppReload({ productKey: props.productKey, reason: "manual", dedupeKey: String(Date.now()) })}>{text.Reload}</Button></div>}
        {(unavailable || (!state.manualReload && state.delayed)) && <div><Button type="primary" size="large" onClick={state.retry}>{text["Retry startup check"]}</Button></div>}
      </div>
    </FullScreenWaitBackdrop>
  );
}
